/**
 * Extract a user-facing error message from API error or response.
 * Handles backend shapes: { error }, { message }, { success: false, ... }, express-validator/zod arrays.
 * @param {Error|{ response?: { data?: any }, message?: string }|any} err
 * @returns {string}
 */
export function getApiErrorMessage(err) {
  const data = err?.response?.data;

  // backend may send { error }, { message }, { success: false, ... } or plain string
  if (typeof data === "string" && data.trim()) return data.trim();

  if (data?.error) return typeof data.error === "string" ? data.error : String(data.error);
  if (data?.message) return typeof data.message === "string" ? data.message : String(data.message);

  // express-validator / zod style arrays
  if (Array.isArray(data?.errors) && data.errors.length) {
    const first = data.errors[0];
    return first?.message || first?.msg || (typeof first === "string" ? first : JSON.stringify(first));
  }

  // sometimes nested
  if (data?.data?.error) return typeof data.data.error === "string" ? data.data.error : String(data.data.error);
  if (data?.data?.message) return typeof data.data.message === "string" ? data.data.message : String(data.data.message);

  // axios / fetch network
  if (err?.message) return err.message;

  return "Operation failed. Please try again.";
}

/**
 * Extract error message for bulk upload responses.
 * Backend may send: { errors: [{ rowIndex, error }] }, { error }, { message }.
 * @param {Error|{ response?: { data?: any }, data?: any }|any} err
 * @returns {string}
 */
export function getUploadErrorMsg(err) {
  const data = err?.response?.data ?? err?.data ?? err;

  // backend sends: { errors: [{ rowIndex, error }] }
  if (Array.isArray(data?.errors) && data.errors.length) {
    const e = data.errors[0];
    const row = e?.rowIndex ?? e?.row ?? null;
    const msg = e?.error ?? e?.message;
    if (msg) return row != null ? `Row ${row}: ${msg}` : String(msg);
  }

  if (data?.error) return typeof data.error === "string" ? data.error : String(data.error);
  if (data?.message) return typeof data.message === "string" ? data.message : String(data.message);
  if (typeof data === "string" && data.trim()) return data.trim();

  return "Upload failed";
}
